import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  User, 
  Mail, 
  Phone, 
  Trash2,
  UserPlus,
  UserCheck
} from 'lucide-react';

/**
 * ParticipantForm Component
 * Handles collecting participant details for webinar registrations
 */
export const ParticipantForm = ({
  participants,
  setParticipants,
  maxParticipants,
  currentUser,
  itemTitle,
  variant,
}) => {
  const addParticipant = () => {
    if (participants.length < maxParticipants) {
      setParticipants([
        ...participants,
        {
          id: Date.now(),
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          is_self: false,
        },
      ]);
    }
  };

  const removeParticipant = (id) => {
    if (participants.length > 1) {
      setParticipants(participants.filter((p) => p.id !== id));
    }
  };

  const updateParticipant = (id, field, value) => {
    setParticipants(
      participants.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
  };

  const toggleSelf = (id, checked) => {
    setParticipants(
      participants.map((p) => {
        if (p.id === id) {
          if (checked && currentUser) {
            return {
              ...p,
              is_self: true,
              first_name: currentUser.first_name || '',
              last_name: currentUser.last_name || '',
              email: currentUser.email || '',
              phone: currentUser.phone || '',
            };
          }
          return {
            ...p,
            is_self: false,
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
          };
        }
        // Uncheck other participants if they were marked as self
        if (checked && p.is_self) {
          return { ...p, is_self: false };
        }
        return p;
      })
    );
  };

  return (
    <Card className="border-slate-200 rounded-xl">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-navy-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Participant Details
          </div>
          <span className="text-sm font-normal text-slate-500">
            {participants.length} of {maxParticipants} participants
          </span>
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Enter details for each participant attending: <strong>{itemTitle}</strong>
          <br />
          <span className="text-xs text-slate-400">Variant: {variant}</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {participants.map((participant, index) => (
          <div
            key={participant.id}
            className="p-4 bg-slate-50 rounded-xl space-y-4 relative"
          >
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-navy-700">
                Participant {index + 1}
              </h4>
              <div className="flex items-center gap-2">
                {currentUser && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`self-${participant.id}`}
                      checked={participant.is_self}
                      onCheckedChange={(checked) => toggleSelf(participant.id, checked)}
                    />
                    <label
                      htmlFor={`self-${participant.id}`}
                      className="text-sm text-slate-600 cursor-pointer flex items-center gap-1"
                    >
                      <UserCheck className="w-4 h-4" />
                      This is me
                    </label>
                  </div>
                )}
                {participants.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeParticipant(participant.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`first_name-${participant.id}`}>
                  First Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id={`first_name-${participant.id}`}
                    value={participant.first_name}
                    onChange={(e) =>
                      updateParticipant(participant.id, 'first_name', e.target.value)
                    }
                    placeholder="John"
                    className="pl-10"
                    disabled={participant.is_self}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`last_name-${participant.id}`}>
                  Last Name *
                </Label>
                <Input
                  id={`last_name-${participant.id}`}
                  value={participant.last_name}
                  onChange={(e) =>
                    updateParticipant(participant.id, 'last_name', e.target.value)
                  }
                  placeholder="Doe"
                  disabled={participant.is_self}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`email-${participant.id}`}>Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id={`email-${participant.id}`}
                    type="email"
                    value={participant.email}
                    onChange={(e) =>
                      updateParticipant(participant.id, 'email', e.target.value)
                    }
                    placeholder="john@example.com"
                    className="pl-10"
                    disabled={participant.is_self}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`phone-${participant.id}`}>Phone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id={`phone-${participant.id}`}
                    type="tel"
                    value={participant.phone}
                    onChange={(e) =>
                      updateParticipant(participant.id, 'phone', e.target.value)
                    }
                    placeholder="(555) 123-4567"
                    className="pl-10"
                    disabled={participant.is_self}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        {participants.length < maxParticipants && (
          <Button
            type="button"
            variant="outline"
            className="w-full border-dashed border-2 border-slate-300 text-slate-600 hover:border-navy-400 hover:text-navy-600"
            onClick={addParticipant}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Another Participant ({participants.length}/{maxParticipants})
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantForm;
